// LCP (Largest Contentful Paint) optimization utilities

// Critical image preloading for LCP
export const preloadLCPImages = () => {
  const lcpImages = [
    '/src/assets/HeroImage1.png',
    '/src/assets/HeroImage2.png',
    '/src/assets/HeroBook1.png'
  ];

  lcpImages.forEach((src, index) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    link.fetchPriority = index === 0 ? 'high' : 'low'; // First image has highest priority
    
    // Add responsive hints
    if (index === 0) {
      link.media = '(min-width: 768px)';
    }
    
    document.head.appendChild(link);
  });
};

// Monitor LCP and provide insights
export const monitorLCP = () => {
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          console.log(`LCP: ${Math.round(entry.startTime)}ms`);
          console.log('LCP Element:', entry.element);
          
          // Send to analytics if needed
          if (window.gtag) {
            window.gtag('event', 'timing_complete', {
              name: 'LCP',
              value: Math.round(entry.startTime)
            });
          }
        }
      }
    });

    try {
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
    } catch (e) {
      console.log('LCP monitoring not supported');
    }
  }
};

// Optimize image loading based on connection speed
export const getOptimalImageFormat = () => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  
  if (!connection) {
    return 'webp'; // Default to WebP if connection info not available
  }

  const effectiveType = connection.effectiveType;
  
  switch (effectiveType) {
    case 'slow-2g':
    case '2g':
      return 'jpeg'; // Use JPEG for slow connections
    case '3g':
      return 'webp'; // Use WebP for moderate connections
    case '4g':
    default:
      return 'avif'; // Use AVIF for fast connections
  }
};

// Dynamic image quality based on device and connection
export const getOptimalImageQuality = () => {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const isHighDPI = window.devicePixelRatio > 1.5;
  
  if (!connection) {
    return isHighDPI ? 85 : 80;
  }

  const effectiveType = connection.effectiveType;
  
  switch (effectiveType) {
    case 'slow-2g':
    case '2g':
      return 60; // Lower quality for slow connections
    case '3g':
      return isHighDPI ? 75 : 70;
    case '4g':
    default:
      return isHighDPI ? 90 : 85;
  }
};

// Responsive image sizes for different viewports
export const generateResponsiveSizes = (baseWidth = 100) => {
  return `(max-width: 480px) ${baseWidth}vw, (max-width: 768px) ${Math.min(baseWidth * 1.2, 100)}vw, ${Math.min(baseWidth * 1.5, 100)}vw`;
};

// Create responsive srcSet for different image formats
export const createResponsiveSrcSet = (baseSrc, formats = ['avif', 'webp', 'png']) => {
  const baseName = baseSrc.replace(/\.(png|jpg|jpeg)$/i, '');
  const srcSets = {};

  formats.forEach(format => {
    const sizes = ['480w', '768w', '1200w', '1920w'];
    srcSets[format] = sizes.map(size => {
      const width = size.replace('w', '');
      return `${baseName}-${width}.${format} ${size}`;
    }).join(', ');
  });

  return srcSets;
};

// Preload critical CSS for LCP elements
export const preloadCriticalCSS = () => {
  const criticalCSS = `
    /* Critical CSS for LCP elements */
    .hero-section {
      min-height: 60vh;
      background-color: #f8f9fa;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .hero-image {
      max-width: 100%;
      height: auto;
      object-fit: cover;
      object-position: center;
    }
    
    .swiper-container {
      width: 100%;
      height: 100%;
    }
    
    .loading-placeholder {
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
    }
    
    @keyframes loading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
  `;

  const style = document.createElement('style');
  style.textContent = criticalCSS;
  document.head.appendChild(style);
};

// Optimize hero section specifically for LCP
export const optimizeHeroForLCP = () => {
  // Mark the hero section for browser optimization
  const heroSection = document.querySelector('[data-hero]');
  if (heroSection) {
    heroSection.style.containIntrinsicSize = '100vw 60vh';
    heroSection.style.contentVisibility = 'visible';
  }

  // Ensure critical images are prioritized
  const heroImages = document.querySelectorAll('[data-lcp-image]');
  heroImages.forEach((img, index) => {
    img.fetchPriority = index === 0 ? 'high' : 'low';
    img.loading = index === 0 ? 'eager' : 'lazy';
  });
};

// Initialize all LCP optimizations
export const initializeLCPOptimizations = () => {
  // Run immediately
  preloadLCPImages();
  preloadCriticalCSS();
  
  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      optimizeHeroForLCP();
      monitorLCP();
    });
  } else {
    optimizeHeroForLCP();
    monitorLCP();
  }
};

// Export configuration for build tools
export const LCP_CONFIG = {
  criticalImages: [
    '/src/assets/HeroImage1.png',
    '/src/assets/HeroImage2.png',
    '/src/assets/HeroBook1.png'
  ],
  targetLCP: 2500, // 2.5 seconds target
  imageSizes: {
    hero: {
      mobile: 480,
      tablet: 768,
      desktop: 1200,
      large: 1920
    }
  },
  formats: ['avif', 'webp', 'png'],
  quality: {
    avif: 70,
    webp: 85,
    png: 90
  }
};
